import { useEffect, useRef, useState } from 'react';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/getModifierState#modifier_keys_on_gecko
 */
export type Modifiers = 'Control' | 'Alt' | 'Shift' | 'Meta'; // extend if you wanna

const isModifierPressed: Record<Modifiers, (e: KeyboardEvent) => boolean> = {
  Control: (e) => e.ctrlKey,
  Alt: (e) => e.altKey,
  Shift: (e) => e.shiftKey,
  Meta: (e) => e.metaKey,
};

export const useKeyCombinationTrigger = (
  modifier: Modifiers,
  key: string,
  onTrigger: () => void,
) => {
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  useEffect(() => {
    const downHandler = (e: KeyboardEvent) => {
      // Skip if the user is typing in an input
      const target = e.target as HTMLInputElement;
      if (
        ['text', 'search', 'textarea', 'number'].includes(target?.type) ||
        target?.isContentEditable
      ) {
        return;
      }

      const modifierMatch = isModifierPressed[modifier](e);
      // On Mac, Alt+Letter often produces a special character (e.g. Alt+P -> π).
      // e.key will be π, but e.code will be 'KeyP'.
      const keyMatch =
        e.key.toLowerCase() === key.toLowerCase() ||
        (modifier === 'Alt' && e.code === `Key${key.toUpperCase()}`);

      if (modifierMatch && keyMatch) {
        e.preventDefault();
        e.stopPropagation();
        onTriggerRef.current();
      }
    };

    window.addEventListener('keydown', downHandler);
    return () => window.removeEventListener('keydown', downHandler);
  }, [modifier, key]);
};

/**
 * This hook works as a toggle: when the key combination is pressed the first time it enters the mode, when it is pressed
 * the second time it exits the mode.
 */
export const useKeyCombination = (modifier: Modifiers, key: string): boolean => {
  const [combinationPressed, setCombinationPressed] = useState(false);

  useKeyCombinationTrigger(modifier, key, () => {
    setCombinationPressed((state) => !state);
  });

  return combinationPressed;
};
